import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile,
  getAuth,
  deleteUser,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import AnalyticsService, { EVENTS } from '../services/firebase/analytics';
import errorTracking from '../services/firebase/errorTracking';
import { app } from '../firebase';
import PropTypes from 'prop-types';
import UserDeletionService from '../services/userDeletion';
import { getFriendlyAuthError } from '../utils/authErrors';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const auth = getAuth(app);

  async function signup(email, password, displayName) {
    try {
      const startTime = performance.now();
      const safeDisplayName = String(displayName || '')
        .trim()
        || String(email || '').split('@')[0]
        || 'Member';
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: safeDisplayName });
      
      // Create user document in Firestore
      // Email stays in Firebase Auth only — not copied into Firestore profiles.
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        displayName: safeDisplayName,
        points: 0,
        badges: [],
        preferences: {
          notifications: true,
          theme: 'light',
        },
        createdAt: new Date().toISOString(),
      });

      const duration = performance.now() - startTime;
      AnalyticsService.logTiming('signup', duration);
      AnalyticsService.logEvent(EVENTS.SIGN_UP, { method: 'email' });

      return userCredential.user;
    } catch (err) {
      setError(getFriendlyAuthError(err, 'Registration failed. Please try again.'));
      errorTracking.logError(err, 'signup', { errorCode: err.code });
      throw err;
    }
  }

  async function login(email, password) {
    try {
      const startTime = performance.now();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Repair profiles if signup created Auth but failed before Firestore write.
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        const safeDisplayName = String(userCredential.user.displayName || '')
          .trim()
          || String(userCredential.user.email || email || '').split('@')[0]
          || 'Member';
        await setDoc(userDocRef, {
          displayName: safeDisplayName,
          points: 0,
          badges: [],
          preferences: {
            notifications: true,
            theme: 'light',
          },
          createdAt: new Date().toISOString(),
        });
      }
      
      const duration = performance.now() - startTime;
      AnalyticsService.logTiming('login', duration);
      AnalyticsService.logEvent(EVENTS.LOGIN, { method: 'email' });

      return userCredential.user;
    } catch (err) {
      setError(getFriendlyAuthError(err, 'Login failed. Please try again.'));
      
      // Log error code only — never the attempted email/password.
      errorTracking.logError(err, 'login', {
        errorCode: err.code
      });
      
      throw err;
    }
  }

  async function loginWithGoogle() {
    try {
      const startTime = performance.now();
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      
      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      
      if (!userDoc.exists()) {
        // Create user document if it doesn't exist
        // Email stays in Firebase Auth only — not copied into Firestore profiles.
        const safeDisplayName = String(userCredential.user.displayName || '')
          .trim()
          || String(userCredential.user.email || '').split('@')[0]
          || 'Member';
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          displayName: safeDisplayName,
          points: 0,
          badges: [],
          preferences: {
            notifications: true,
            theme: 'light',
          },
          createdAt: new Date().toISOString(),
        });
      }

      const duration = performance.now() - startTime;
      AnalyticsService.logTiming('google_login', duration);
      AnalyticsService.logEvent(EVENTS.LOGIN, { method: 'google' });

      return userCredential.user;
    } catch (err) {
      setError(getFriendlyAuthError(err, 'Google sign-in failed. Please try again.'));
      errorTracking.logError(err, 'google_login', { errorCode: err.code });
      throw err;
    }
  }

  async function logout() {
    try {
      const startTime = performance.now();
      await signOut(auth);
      
      const duration = performance.now() - startTime;
      AnalyticsService.logTiming('logout', duration);
      AnalyticsService.logEvent(EVENTS.LOGOUT);
    } catch (err) {
      setError(err.message);
      errorTracking.logError(err, 'logout');
      throw err;
    }
  }

  async function resetPassword(email) {
    try {
      const startTime = performance.now();
      await sendPasswordResetEmail(auth, email);
      
      const duration = performance.now() - startTime;
      AnalyticsService.logTiming('reset_password', duration);
      AnalyticsService.logEvent('reset_password_requested');
    } catch (err) {
      setError(err.message);
      errorTracking.logError(err, 'reset_password');
      throw err;
    }
  }

  async function deleteAccount() {
    try {
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }

      const userId = currentUser.uid;
      const startTime = performance.now();

      // Step 1: Delete all user data from Firestore and Storage
      console.log('Deleting user data...');
      const deletionSummary = await UserDeletionService.deleteAllData(userId);
      console.log('Deletion summary:', deletionSummary);

      // Step 2: Delete the Firebase Auth account
      console.log('Deleting Firebase Auth account...');
      await deleteUser(currentUser);

      const duration = performance.now() - startTime;
      AnalyticsService.logTiming('account_deletion', duration);
      AnalyticsService.logEvent('account_deleted', { 
        itemsDeleted: deletionSummary.items,
        wasteLogsDeleted: deletionSummary.wasteLogs,
        postsDeleted: deletionSummary.posts
      });

      // Clear current user state
      setCurrentUser(null);

      return { success: true, summary: deletionSummary };
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.message);
      errorTracking.logError(err, 'account_deletion');
      throw err;
    }
  }

  async function updateUserProfile(data) {
    try {
      const startTime = performance.now();
      if (currentUser) {
        console.log('Updating user profile with data:', data);
        // Only update displayName and photoURL in Firebase Auth
        const authData = {};
        if (data.displayName !== undefined) authData.displayName = data.displayName;
        if (data.photoURL !== undefined) authData.photoURL = data.photoURL;
        console.log('Updating auth profile with:', authData);
        await updateProfile(currentUser, authData);
        console.log('Auth profile updated successfully');
        // Save all data (including avatarSVG) to Firestore
        const userDocRef = doc(db, 'users', currentUser.uid);
        console.log('Updating Firestore document for user:', currentUser.uid);
        const profileData = { ...(data || {}) };
        delete profileData.email;
        await setDoc(userDocRef, {
          ...profileData,
          updatedAt: new Date().toISOString()
        }, { merge: true });
        console.log('Firestore document updated successfully');
        // Update local user object to reflect changes immediately
        if (data.displayName) currentUser.displayName = data.displayName;
        if (data.photoURL) currentUser.photoURL = data.photoURL;
        // --- NEW: Re-fetch user document and update currentUser ---
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setCurrentUser(Object.assign(currentUser, { profile: userDoc.data() }));
        }
      }
      const duration = performance.now() - startTime;
      AnalyticsService.logTiming('update_profile', duration);
      AnalyticsService.logEvent('profile_updated');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
      errorTracking.logError(err, 'update_profile');
      throw err;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Get user data from Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const profile = { ...userDoc.data() };
            // Remove legacy email copies from Firestore (email lives in Auth).
            if (Object.prototype.hasOwnProperty.call(profile, 'email')) {
              delete profile.email;
              updateDoc(userDocRef, { email: deleteField() }).catch(() => {});
            }
            setCurrentUser(Object.assign(user, { profile }));
          } else {
            setCurrentUser(user);
          }
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        errorTracking.logError(err, 'auth_state_change');
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [auth]);

  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    updateUserProfile,
    deleteAccount,
    error,
    setError,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  return useContext(AuthContext);
} 